/**
 * Settings tab UI.
 *
 * Three sections:
 *   - Account: email + sign-out
 *   - PDF retention: server-synced toggle
 *   - My data: delete-my-retained-data with confirm
 */
import { deleteJson, getJson, patchJson } from "../lib/outbound";
import { getAuthState, onAuthStateChange, signOut } from "../lib/auth";
import { copyDiagnosticsToClipboard } from "../lib/diagnostics";

const SERVER_URL = process.env.EXTRACT_SERVER_URL.replace(/\/+$/, "");

interface MeResponse {
  email: string;
  pdf_retention_enabled: boolean;
}

interface DeleteResponse {
  deleted_count: number;
}

let initialized = false;
let cachedMe: MeResponse | null = null;

export async function initSettingsUI(): Promise<void> {
  if (initialized) return;
  initialized = true;

  const panel = document.querySelector<HTMLElement>('[data-tab-panel="settings"]');
  if (!panel) return;

  wireAccount();
  wireRetention();
  wireDelete();
  wireCopyDiagnostics();

  // Refresh from server when the user clicks the Settings tab.
  const tabBtn = document.querySelector<HTMLButtonElement>('.tab[data-tab="settings"]');
  if (tabBtn) {
    tabBtn.addEventListener("click", () => {
      void refreshFromServer();
    });
  }
}

function wireAccount(): void {
  const emailEl = document.getElementById("settings-email");

  function render(email: string | null): void {
    if (emailEl) emailEl.textContent = email ?? "—";
  }

  const state = getAuthState();
  render(state.kind === "signed_in" ? state.email : null);

  onAuthStateChange((s) => {
    render(s.kind === "signed_in" ? s.email : null);
  });

  document.getElementById("settings-signout")?.addEventListener("click", async () => {
    try {
      await signOut();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      flashStatus("settings-account-status", `Sign-out failed: ${msg}`, "error");
    }
  });
}

function wireRetention(): void {
  const toggle = document.getElementById("settings-pdf-retention") as HTMLInputElement | null;
  const help = document.getElementById("settings-pdf-retention-current");
  if (!toggle) return;

  toggle.disabled = true; // until first server fetch

  toggle.addEventListener("change", async () => {
    const desired = toggle.checked;
    toggle.disabled = true;
    flashStatus("settings-pdf-retention-status", "Updating…");
    try {
      const updated = await patchJson<MeResponse, { pdf_retention_enabled: boolean }>(
        `${SERVER_URL}/api/me`,
        { pdf_retention_enabled: desired },
      );
      cachedMe = updated;
      toggle.checked = updated.pdf_retention_enabled;
      if (help) help.textContent = stateLabel(updated.pdf_retention_enabled);
      flashStatus(
        "settings-pdf-retention-status",
        `Now ${updated.pdf_retention_enabled ? "enabled" : "disabled"}.`,
        "ok",
      );
    } catch (err) {
      // Revert UI to whatever the server last told us.
      const lastKnown = cachedMe?.pdf_retention_enabled ?? !desired;
      toggle.checked = lastKnown;
      const msg = err instanceof Error ? err.message : String(err);
      flashStatus("settings-pdf-retention-status", `Update failed: ${msg}`, "error");
    } finally {
      toggle.disabled = false;
    }
  });
}

function wireDelete(): void {
  const btn = document.getElementById("settings-delete-data") as HTMLButtonElement | null;
  if (!btn) return;

  btn.addEventListener("click", async () => {
    const ok = window.confirm(
      "Delete every PDF you've uploaded that's still retained on our server? " +
        "This takes effect immediately and can't be undone.",
    );
    if (!ok) return;

    btn.disabled = true;
    flashStatus("settings-delete-status", "Deleting…");
    try {
      const result = await deleteJson<DeleteResponse>(`${SERVER_URL}/api/pdf-corpus/mine`);
      const n = result.deleted_count;
      flashStatus(
        "settings-delete-status",
        `Deleted ${n} retained PDF${n === 1 ? "" : "s"}.`,
        "ok",
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      flashStatus("settings-delete-status", `Delete failed: ${msg}`, "error");
    } finally {
      btn.disabled = false;
    }
  });
}

function wireCopyDiagnostics(): void {
  const btn = document.getElementById("settings-copy-diagnostics") as HTMLButtonElement | null;
  if (!btn) return;

  btn.addEventListener("click", async () => {
    btn.disabled = true;
    flashStatus("settings-diagnostics-status", "Building packet…");
    try {
      const bytes = await copyDiagnosticsToClipboard();
      flashStatus(
        "settings-diagnostics-status",
        `Copied ${bytes.toLocaleString()} bytes to clipboard. Paste it into your support email.`,
        "ok",
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      flashStatus(
        "settings-diagnostics-status",
        `Couldn't copy: ${msg}. Try again or contact support directly.`,
        "error",
      );
    } finally {
      btn.disabled = false;
    }
  });
}

async function refreshFromServer(): Promise<void> {
  const toggle = document.getElementById("settings-pdf-retention") as HTMLInputElement | null;
  const help = document.getElementById("settings-pdf-retention-current");
  if (!toggle) return;
  try {
    const me = await getJson<MeResponse>(`${SERVER_URL}/api/me`);
    cachedMe = me;
    toggle.checked = me.pdf_retention_enabled;
    toggle.disabled = false;
    if (help) help.textContent = stateLabel(me.pdf_retention_enabled);
  } catch (err) {
    toggle.disabled = true;
    const msg = err instanceof Error ? err.message : String(err);
    flashStatus("settings-pdf-retention-status", `Couldn't load: ${msg}`, "error");
  }
}

function stateLabel(enabled: boolean): string {
  return enabled
    ? "Retention is currently ON. New uploads are kept on our server."
    : "Retention is currently OFF. PDFs you upload are processed and discarded.";
}

function flashStatus(
  id: string,
  message: string,
  kind: "ok" | "error" | "info" = "info",
): void {
  const el = document.getElementById(id);
  if (!el) return;
  el.textContent = message;
  el.className = `settings-status settings-status-${kind}`;
  if (kind === "ok") {
    setTimeout(() => {
      if (el.textContent === message) {
        el.textContent = "";
        el.className = "settings-status";
      }
    }, 3500);
  }
}
